/*
 * The shape and validation of /app-config.json, the per-environment settings
 * Admin South Downs reads at startup (ADR-034). Every field is required and has
 * no default: a deployment that omits one fails to start rather than signing
 * people in against another environment's tenant.
 *
 * This file is also run by Node in the deploy pipeline
 * (scripts/deploy/write-app-config.mjs), so it has no imports and uses only
 * TypeScript syntax that Node's type stripping accepts.
 */

export type RuntimeConfig = {
  auth0: {
    /** Auth0 tenant hostname, e.g. visit-prod.eu.auth0.com. */
    domain: string
    /** Public SPA client id (Authorization Code + PKCE). */
    clientId: string
    audience: string
  }
  bff: {
    /** Origin of the VisitOne API that hosts the Admin BFF, e.g. https://api.visitonelink.com. */
    hostname: string
    /** Environment-scoped x-visit-key sent on every BFF request. */
    visitKey: string
  }
}

export type ConfigProblemReason = 'missing' | 'invalid' | 'unreachable' | 'malformed'

export type ConfigProblem = {
  /** Dotted field path, or `app-config.json` for a problem with the file itself. */
  field: string
  reason: ConfigProblemReason
}

export type ParseResult = { ok: true; config: RuntimeConfig } | { ok: false; problems: ConfigProblem[] }

export type ParseOptions = {
  /** Accept http://localhost and http://127.0.0.1 as the BFF origin, for local development only. */
  allowLoopbackHttp?: boolean
}

export const RUNTIME_CONFIG_FILE = 'app-config.json'

const FIELDS = ['auth0.domain', 'auth0.clientId', 'auth0.audience', 'bff.hostname', 'bff.visitKey'] as const

type Field = (typeof FIELDS)[number]

const AUTH0_DOMAIN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1'])

function read(raw: unknown, field: Field): unknown {
  const [section, key] = field.split('.')
  if (!raw || typeof raw !== 'object') return undefined
  const group = (raw as Record<string, unknown>)[section]
  if (!group || typeof group !== 'object') return undefined
  return (group as Record<string, unknown>)[key]
}

/** The BFF origin with no trailing slash, or null when it is not an acceptable origin. */
function bffOrigin(value: string, options: ParseOptions): string | null {
  let url: URL
  try {
    url = new URL(value)
  } catch {
    return null
  }
  const secure = url.protocol === 'https:'
  const loopback = options.allowLoopbackHttp === true && url.protocol === 'http:' && LOOPBACK_HOSTS.has(url.hostname)
  if (!secure && !loopback) return null
  if (url.username || url.password || url.search || url.hash || url.pathname !== '/') return null
  return url.origin
}

/**
 * Validates a parsed app-config.json. Strings are trimmed; the BFF hostname is
 * normalised to its origin. Every problem is reported, not just the first.
 */
export function parseRuntimeConfig(raw: unknown, options: ParseOptions = {}): ParseResult {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ok: false, problems: [{ field: RUNTIME_CONFIG_FILE, reason: 'malformed' }] }
  }

  const problems: ConfigProblem[] = []
  const values: Partial<Record<Field, string>> = {}

  for (const field of FIELDS) {
    const value = read(raw, field)
    if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
      problems.push({ field, reason: 'missing' })
      continue
    }
    if (typeof value !== 'string') {
      problems.push({ field, reason: 'invalid' })
      continue
    }
    values[field] = value.trim()
  }

  const domain = values['auth0.domain']
  if (domain !== undefined && !AUTH0_DOMAIN.test(domain)) {
    problems.push({ field: 'auth0.domain', reason: 'invalid' })
  }

  const hostname = values['bff.hostname']
  const origin = hostname === undefined ? null : bffOrigin(hostname, options)
  if (hostname !== undefined && origin === null) {
    problems.push({ field: 'bff.hostname', reason: 'invalid' })
  }

  if (problems.length > 0 || origin === null) return { ok: false, problems }

  return {
    ok: true,
    config: {
      auth0: {
        domain: values['auth0.domain'] as string,
        clientId: values['auth0.clientId'] as string,
        audience: values['auth0.audience'] as string,
      },
      bff: {
        hostname: origin,
        visitKey: values['bff.visitKey'] as string,
      },
    },
  }
}
